import { useCallback, useEffect, useState } from "react";
import { naytaPvm } from "./pvm";
import { nayttonimi } from "./nayttonimi";
import { api, ApiError, type LaiteTila } from "./api";
import { t, virheTeksti } from "./kieli";

/* ═══════════════════════════════════════════════════════════
   Laitteen selailu — mitkä 14, ei vain 13/14.

   Listassa luki `13/14 liitosta`, eikä mistään näkynyt **mitkä**.
   Data oli API:ssa (`GET /api/laitteet/:id` palauttaa vaiheet,
   liitokset ja ristiriidat), näkymää ei ollut. Sama muoto kuin kaikki
   muutkin tämän päivän löydökset: tieto on olemassa eikä mikään näytä
   sitä.

   Kolme asiaa jotka tämä näyttää ja lista ei:

     · mitkä liitokset ovat auki, tunnuksineen ja momentteineen
     · primääripiiri erikseen merkittynä — se EI kuulu urakkaan eikä
       luovutusaineistoon, ja sen on näyttävä siltä
     · ristiriita: vaihe kuitattu valmiiksi vaikka liitoksia on auki.
       Ei estetä, näytetään — ihminen ratkaisee.
   ═══════════════════════════════════════════════════════════ */

function nm(arvo: string | number | null): string {
  if (arvo === null || arvo === "") return "—";
  const n = typeof arvo === "string" ? Number(arvo) : arvo;
  return Number.isFinite(n) ? `${n} Nm` : "—";
}


export default function LaiteSelailu({
  laiteId, tyonjohto, muuttui,
}: { laiteId: number; tyonjohto: boolean; muuttui: () => void }) {
  const [tila, setTila] = useState<LaiteTila | null>(null);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [tyoskentelee, setTyoskentelee] = useState(false);

  const lataa = useCallback(async () => {
    try {
      setTila(await api.laite(laiteId));
    } catch (e) {
      setVirhe(e instanceof ApiError ? e.message : String(e));
    }
  }, [laiteId]);

  useEffect(() => { void lataa(); }, [lataa]);

  async function vaihe(vaiheId: number, kuitattu: boolean) {
    if (tyoskentelee) return;
    setTyoskentelee(true);
    setVirhe(null);
    try {
      if (kuitattu) await api.peruVaihekuittaus(laiteId, vaiheId);
      else await api.kuittaaVaihe(laiteId, vaiheId);
      await lataa();
      muuttui();
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("muu.laippa.kuittausvirhe"));
    } finally {
      setTyoskentelee(false);
    }
  }

  if (virhe && !tila) return <p className="virhe">{virhe}</p>;
  if (!tila) return <p>{t("yleinen.ladataan")}</p>;

  const sekundaari = tila.liitokset.filter((l) => l.piiri === "sekundaari");
  const primaari = tila.liitokset.filter((l) => l.piiri === "primaari");
  const lajit = [...new Set(tila.vaiheet.map((v) => v.laji))];

  return (
    <section className="panel laiteselailu">
      <div className="panel__head"><h2>{t("muu.ls.otsikko")}</h2></div>
      {virhe && <p className="virhe">{virhe}</p>}

      {/* Ristiriita ensin: se on ainoa asia tällä sivulla joka vaatii
          ihmisen päätöksen. */}
      {tila.ristiriidat.length > 0 && (
        <div className="ristiriita">
          <strong>{t("muu.ls.ristiriita")}</strong>
          <ul>
            {tila.ristiriidat.map((r, i) => (
              <li key={i}>{r.vaihe}: {r.syy}</li>
            ))}
          </ul>
          <p className="hint">
            {t("muu.ls.ristiriita_selite")}
          </p>
        </div>
      )}

      {tila.ketjut.length === 0 && (
        <p className="tyhja">{t("muu.ls.ei_ketjua")}</p>
      )}

      {lajit.map((laji) => {
        const vaiheet = tila.vaiheet.filter((v) => v.laji === laji);
        const kuitattu = vaiheet.filter((v) => v.kuitattu !== null).length;
        return (
          <div key={laji} className="vaiheryhma">
            <h3>
              {nayttonimi("laite_laji", laji)}
              {/* Johdettu luku, ei tallennettu valmiusaste. */}
              <span className="dim"> {kuitattu}/{vaiheet.length}</span>
            </h3>
            <ul className="vaihelista">
              {vaiheet.map((v) => (
                <li key={v.id} className={v.kuitattu ? "vaihe vaihe--ok" : "vaihe"}>
                  <span className="vaihe__nimi">{v.nimi}</span>
                  <span className="vaihe__tieto">
                    {v.kuitattu
                      ? `${naytaPvm(v.paiva) ?? ""}${v.kuittaaja ? ` · ${v.kuittaaja}` : ""}`
                      : t("muu.ls.kuittaamatta")}
                  </span>
                  {tyonjohto && (
                    <button className="nappi nappi--kevyt" disabled={tyoskentelee}
                            onClick={() => void vaihe(v.id, v.kuitattu !== null)}>
                      {v.kuitattu ? t("muu.ls.peru") : t("muu.laippa.kuittaa")}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        );
      })}

      <h3>{t("muu.ls.liitokset")} <span className="dim">
        {t("muu.ls.sekundaarista", { k: sekundaari.filter((l) => l.kuitattu).length, n: sekundaari.length })}
      </span></h3>
      <table className="taulu">
        <thead>
          <tr><th>{t("iv.tunnus")}</th><th>{t("muu.lk.kuvaus")}</th><th>{t("muu.lk.momentti")}</th><th>{t("muu.ls.kuitattu")}</th><th>{t("muu.ls.kuittaaja")}</th></tr>
        </thead>
        <tbody>
          {sekundaari.map((l) => (
            <tr key={l.id} className={l.kuitattu ? undefined : "tr--auki"}>
              <td className="mono">{l.tunnus}</td>
              <td>{l.kuvaus ?? "—"}{l.huomiot && <div className="dim">{l.huomiot}</div>}</td>
              <td className="mono">{nm(l.vaadittu_nm)}</td>
              <td>{naytaPvm(l.paiva) ?? <span className="dim">{t("muu.ls.auki")}</span>}</td>
              <td>{l.kuittaaja ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Primääripiiri erikseen ja merkittynä: se on laitoksen urakkaa
          eikä kuulu luovutusaineistoon. Jos se olisi samassa
          taulukossa, lukija laskisi sen mukaan. */}
      {primaari.length > 0 && (
        <>
          <h3>{t("muu.ls.primaari")} <span className="dim">— {t("muu.ls.primaari_selite")}</span></h3>
          <table className="taulu taulu--vaimea">
            <thead><tr><th>{t("iv.tunnus")}</th><th>{t("muu.lk.kuvaus")}</th><th>{t("muu.lk.momentti")}</th></tr></thead>
            <tbody>
              {primaari.map((l) => (
                <tr key={l.id}>
                  <td className="mono">{l.tunnus}</td>
                  <td>{l.kuvaus ?? "—"}</td>
                  <td className="mono">{nm(l.vaadittu_nm)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}
